import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";
import { createApi } from "../api/client";
import { usePricing } from "../hooks/usePricing";

type Job = {
  job_id: string;
  status: "pending" | "paid" | "processing" | "ready" | "failed";
  created_at: number;
  photo_url?: string;
  video_url?: string;
  download_url?: string;
  subject_hint?: string;
  error?: string;
  parent_job_id?: string;
  variation_index?: number;
  variation_count?: number;
};

const STATUS_LABEL: Record<Job["status"], string> = {
  pending: "Awaiting payment",
  paid: "Queued",
  processing: "Bringing to life…",
  ready: "Ready",
  failed: "Failed",
};

function formatDate(unix: number): string {
  if (!unix) return "";
  return new Date(unix * 1000).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export function MyVideos() {
  const { getIdToken } = useAuth();
  const api = createApi(getIdToken);
  const nav = useNavigate();
  const { display: price } = usePricing();
  const [jobs, setJobs] = useState<Job[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [variatingId, setVariatingId] = useState<string | null>(null);

  const fetchJobs = useCallback(async () => {
    try {
      const r = await api.listJobs();
      setJobs(r.jobs);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Could not load your videos");
    }
  }, [api]);

  const onDelete = useCallback(async (jobId: string, label: string) => {
    const ok = window.confirm(
      `Permanently delete "${label}"?\n\nThe photo and video will be removed from our servers. This cannot be undone.`
    );
    if (!ok) return;
    setDeletingId(jobId);
    try {
      await api.deleteJob(jobId);
      setJobs(prev => prev?.filter(j => j.job_id !== jobId) ?? prev);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Could not delete");
    } finally {
      setDeletingId(null);
    }
  }, [api]);

  const onVariation = useCallback(async (jobId: string) => {
    setVariatingId(jobId); setError(null);
    try {
      const r = await api.createVariation(jobId);
      nav(`/jobs/${r.job_id}`);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Could not start variation");
    } finally {
      setVariatingId(null);
    }
  }, [api, nav]);

  useEffect(() => { fetchJobs(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll while any job is still in-flight, so the Status chip updates automatically.
  useEffect(() => {
    if (!jobs) return;
    const inFlight = jobs.some(j => j.status === "paid" || j.status === "processing");
    if (!inFlight) return;
    const t = setInterval(fetchJobs, 5000);
    return () => clearInterval(t);
  }, [jobs, fetchJobs]);

  // Build a map of root_job_id -> variations_remaining from the full job list.
  const remainingByRoot = useMemo(() => {
    const map: Record<string, number> = {};
    if (!jobs) return map;
    for (const j of jobs) {
      if (!j.parent_job_id) {
        const used = j.variation_count ?? 0;
        map[j.job_id] = Math.max(0, 2 - used);
      }
    }
    return map;
  }, [jobs]);

  if (error) {
    return (
      <section className="mx-auto max-w-3xl px-6 py-16 text-center">
        <p className="text-red-600">{error}</p>
      </section>
    );
  }

  if (!jobs) {
    return (
      <section className="mx-auto max-w-3xl px-6 py-16 text-center text-neutral-500">
        Loading your memories…
      </section>
    );
  }

  if (jobs.length === 0) {
    return (
      <section className="mx-auto max-w-3xl px-6 py-16 text-center">
        <h1 className="font-serif text-4xl tracking-tight">Your memories</h1>
        <p className="mt-3 text-neutral-600">You haven't made one yet.</p>
        <Link
          to="/upload"
          className="mt-8 inline-flex items-center gap-2 rounded-full bg-black text-white px-6 py-2.5 text-sm font-medium hover:bg-neutral-800 transition"
        >
          Give a memory — {price}
        </Link>
      </section>
    );
  }

  return (
    <section className="mx-auto max-w-6xl px-6 py-12">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-serif text-4xl tracking-tight">Your memories</h1>
          <p className="mt-2 text-neutral-600">
            Every photo you've sent us, and the video we made from it.
          </p>
        </div>
        <Link
          to="/upload"
          className="rounded-full bg-black text-white px-5 py-2.5 text-sm font-medium hover:bg-neutral-800 transition whitespace-nowrap"
        >
          Make another — {price}
        </Link>
      </div>

      <ul className="mt-10 grid sm:grid-cols-2 gap-8">
        {jobs.map(j => {
          const rootId = j.parent_job_id ?? j.job_id;
          const remaining = remainingByRoot[rootId] ?? 0;
          return (
            <JobCard
              key={j.job_id}
              job={j}
              deleting={deletingId === j.job_id}
              variating={variatingId === j.job_id}
              variationsRemaining={remaining}
              onDelete={() => onDelete(j.job_id, j.subject_hint || "this memory")}
              onVariation={() => onVariation(j.job_id)}
            />
          );
        })}
      </ul>
    </section>
  );
}

function JobCard({
  job,
  deleting,
  variating,
  variationsRemaining,
  onDelete,
  onVariation,
}: {
  job: Job;
  deleting: boolean;
  variating: boolean;
  variationsRemaining: number;
  onDelete: () => void;
  onVariation: () => void;
}) {
  const ready = job.status === "ready" && job.video_url;
  const isVariation = !!job.parent_job_id;
  return (
    <li
      className={`rounded-2xl overflow-hidden border border-neutral-200 bg-white transition ${
        deleting ? "opacity-50 pointer-events-none" : ""
      }`}
    >
      <div className="relative aspect-[4/5] bg-neutral-100">
        {ready ? (
          <video
            src={job.video_url}
            poster={job.photo_url}
            controls
            preload="metadata"
            className="absolute inset-0 w-full h-full object-cover bg-black"
          />
        ) : job.photo_url ? (
          <img
            src={job.photo_url}
            alt={job.subject_hint || "Uploaded photo"}
            className="absolute inset-0 w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-sm text-neutral-400">
            Photo no longer available
          </div>
        )}
        <span
          className={`absolute left-3 top-3 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${
            ready
              ? "bg-black/70 text-white"
              : job.status === "failed"
              ? "bg-red-600/90 text-white"
              : "bg-white/90 text-neutral-700"
          }`}
        >
          {STATUS_LABEL[job.status]}
        </span>
        {isVariation && (
          <span className="absolute right-3 top-3 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-white/90 text-neutral-700">
            Variation {job.variation_index ?? ""}
          </span>
        )}
      </div>
      <div className="px-4 py-3 flex items-center justify-between gap-3">
        <div className="min-w-0">
          {job.subject_hint ? (
            <p className="italic text-sm text-neutral-700 truncate">{job.subject_hint}</p>
          ) : (
            <p className="italic text-sm text-neutral-400">Untitled memory</p>
          )}
          <p className="text-xs text-neutral-400">{formatDate(job.created_at)}</p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {ready && variationsRemaining > 0 && (
            <button
              onClick={onVariation}
              disabled={variating}
              className="text-xs font-medium text-neutral-700 hover:text-black transition whitespace-nowrap disabled:opacity-50"
              title={`${variationsRemaining} free variation${variationsRemaining > 1 ? "s" : ""} left`}
            >
              {variating ? "Starting…" : `Variation (${variationsRemaining})`}
            </button>
          )}
          {ready && (job.download_url || job.video_url) && (
            <a
              href={job.download_url || job.video_url}
              className="text-xs font-medium text-neutral-700 hover:text-black transition whitespace-nowrap"
            >
              Download
            </a>
          )}
          {!ready && job.status !== "failed" && (
            <Link
              to={`/jobs/${job.job_id}`}
              className="text-xs font-medium text-neutral-700 hover:text-black transition whitespace-nowrap"
            >
              View status
            </Link>
          )}
          <button
            onClick={onDelete}
            disabled={deleting}
            className="text-xs font-medium text-neutral-400 hover:text-red-600 transition whitespace-nowrap disabled:opacity-50"
            aria-label="Delete this memory"
          >
            {deleting ? "Deleting…" : "Delete"}
          </button>
        </div>
      </div>
      {job.status === "failed" && job.error && (
        <p className="px-4 pb-3 text-xs text-red-600">{job.error}</p>
      )}
    </li>
  );
}
